import { PopperPlacementType } from "@mui/base/Popper";
import { genericMemo } from "@redotech/react-util/component";
import { useHandler } from "@redotech/react-util/hook";
import { getKey } from "@redotech/react-util/key";
import { useTranslation } from "@redotech/redo-i18n";
import ExternalLinkIcon from "@redotech/redo-icons/link-external-02.svg";
import { assertNever } from "@redotech/util/type";
import { ArrowDown, ArrowUp, EnterKey } from "@redotech/web-util/key";
import { useVirtualizer } from "@tanstack/react-virtual";
import * as React from "react";
import {
  Dispatch,
  JSXElementConstructor,
  memo,
  ReactNode,
  SetStateAction,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { Flex } from "../../flex";
import { Text } from "../../text";
import { DisabledTooltip } from "../../tooltip/disabled-tooltip";
import { RedoBadgeProps } from "../badge/redo-badge";
import { RedoCommandMenuItem } from "../command-menu/redo-command-menu";
import { RedoIcon } from "../icon/redo-icon";
import { RedoSpinner } from "../loading/redo-spinner";
import { LeadingItem } from "./redo-list-item-lead";
import * as redoListItemCss from "./redo-list-item.module.css";
import * as redoListCss from "./redo-list.module.css";
import {
  BadgeRedoListItem,
  itemSizeToTextSize,
  ParentRedoListItem,
  RedoListItemContainer,
  RedoListItemSelectionVariant,
  RedoListItemSize,
  TextRedoListItem,
} from "./standard-redo-list-items";

interface BaseRedoListItem<T> {
  id: string | number | symbol;
  value: T;
  menu?: RedoCommandMenuItem[] | { onClick: () => void };
  hotkey?: string[] | string;
  infoBadge?: Omit<RedoBadgeProps, "size">;
  leadingBadge?: Omit<RedoBadgeProps, "size">;
  TrailingIcon?: JSXElementConstructor<any>;
  selectionVariant?: RedoListItemSelectionVariant;
}
export interface TextRedoListItem<T> extends BaseRedoListItem<T> {
  type: "text";
  href?: string;
  text: string;
  supportingText?: string;
  leadingItem?: LeadingItem;
}

export interface SectionRedoListItem<T> extends BaseRedoListItem<T> {
  type: "section";
  text: string;
  externalLink?: string;
}

export interface BadgeRedoListItem<T> extends BaseRedoListItem<T> {
  type: "badge";
  badge: Omit<RedoBadgeProps, "size">;
}

export interface CustomRedoListItem<T> extends BaseRedoListItem<T> {
  type: "custom";
  render: ReactNode;
  useWrapper: boolean;
}

export interface ParentRedoListItem<T> extends BaseRedoListItem<T> {
  type: "parent";
  text: string;
  children: RedoListItem<T>[];
  emptyListMessage?: string;
  fitToAnchor?: boolean;
  leadingItem?: LeadingItem;
  leadingItemSize?: RedoListItemSize;
  subMenuPlacement?: PopperPlacementType;
  onClickSideEffect?: () => void;
}

export type RedoListItem<T> =
  | TextRedoListItem<T>
  | BadgeRedoListItem<T>
  | CustomRedoListItem<T>
  | SectionRedoListItem<T>
  | ParentRedoListItem<T>;

const listItemSelectedSource = ["keyboard", "mouse"] as const;
export type RedoListItemSelectedSource =
  (typeof listItemSelectedSource)[number];

export interface RedoListProps<T> {
  items: RedoListItem<T>[];
  itemSelected(args: {
    item: RedoListItem<T>;
    source: RedoListItemSelectedSource;
    index: number;
    selected: boolean;
  }): void;
  selectedItems?: (string | number | symbol)[] | (string | number | symbol);
  indeterminateItems?:
    | (string | number | symbol)[]
    | (string | number | symbol);
  disabledItems?: (string | number | symbol)[] | (string | number | symbol);
  disabledTooltips?: Record<string | number, string>;
  refToListenTo: HTMLElement | null;
  focusedIndex: number | undefined;
  setFocusedIndex: Dispatch<SetStateAction<number | undefined>>;
  selectionVariant?: RedoListItemSelectionVariant;
  size?: RedoListItemSize;
  itemsLoading?: boolean;
  emptyListMessage?: string;
  card?: boolean;
  customStyles?: React.CSSProperties;
  sortBySelected?: boolean;
  onItemMouseEnter?: (item: RedoListItem<T>) => void;
  onItemMouseLeave?: () => void;
  /**
   * Virtualize rendering so only the visible rows mount. Opt-in for lists with
   * hundreds+ of items. Not compatible with `parent` items (which have their
   * own nested popper) — use flat text/badge/section/custom items only.
   */
  virtualize?: boolean;
  /** Initial row size estimate in px for the virtualizer. Default 32. */
  virtualItemSize?: number;
  /** Max height of the virtualized scroll area in px. Default 320. */
  virtualMaxHeight?: number;
  /** Number of rows to render outside the viewport. Default 8. */
  virtualOverscan?: number;
}

export const RedoList = genericMemo(function RedoList<T>({
  items,
  itemSelected,
  selectedItems,
  indeterminateItems,
  disabledItems,
  disabledTooltips,
  refToListenTo,
  focusedIndex,
  setFocusedIndex,
  selectionVariant = "checkmark",
  size = "sm",
  itemsLoading,
  emptyListMessage: emptyListMessageProp,
  card = false,
  customStyles,
  sortBySelected = false,
  onItemMouseEnter,
  onItemMouseLeave,
  virtualize = false,
  virtualItemSize = 32,
  virtualMaxHeight = 320,
  virtualOverscan = 8,
}: RedoListProps<T>) {
  const { t } = useTranslation();
  const emptyListMessage = emptyListMessageProp ?? t("No items");

  const isItemDisabled = useHandler((item: RedoListItem<T>) => {
    if (!disabledItems) {
      return false;
    }
    if (Array.isArray(disabledItems)) {
      return disabledItems.includes(item.id);
    }
    return disabledItems === item.id;
  });

  const isItemSelected = useHandler((item: RedoListItem<T>): boolean => {
    if (selectedItems === undefined) {
      return false;
    }
    if (Array.isArray(selectedItems)) {
      return selectedItems.includes(item.id);
    }
    return selectedItems === item.id;
  });

  const isItemIndeterminate = useHandler((item: RedoListItem<T>) => {
    if (indeterminateItems === undefined) {
      return false;
    }
    if (Array.isArray(indeterminateItems)) {
      return indeterminateItems.includes(item.id);
    }
    return indeterminateItems === item.id;
  });

  const handleKeyPress = useHandler(
    (event: KeyboardEvent | React.KeyboardEvent) => {
      switch (event.key) {
        case EnterKey: {
          event.preventDefault();
          if (focusedIndex === undefined) {
            return;
          }
          event.stopPropagation();
          const selectedItem = items[focusedIndex];

          // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
          if (selectedItem.type === "parent") {
            return undefined;
          }

          // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
          if (!isItemDisabled(selectedItem)) {
            itemSelected({
              // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
              item: selectedItem,
              source: "keyboard",
              index: focusedIndex,
              // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
              selected: !isItemSelected(selectedItem),
            });
          }

          break;
        }
        case ArrowDown: {
          event.preventDefault();
          const oldIndex = focusedIndex ?? -1;
          let newIndex = Math.min(oldIndex + 1, items.length - 1);
          // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
          while (newIndex < items.length && isItemDisabled(items[newIndex])) {
            newIndex++;
          }
          if (newIndex < items.length) {
            setFocusedIndex(newIndex);
          }

          break;
        }
        case ArrowUp: {
          event.preventDefault();
          const oldIndex = focusedIndex ?? items.length;
          let newIndex = Math.min(oldIndex - 1, items.length - 1);
          // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
          while (newIndex >= 0 && isItemDisabled(items[newIndex])) {
            newIndex--;
          }
          if (newIndex >= 0) {
            setFocusedIndex(newIndex);
          } else {
            setFocusedIndex(undefined);
          }

          break;
        }
        // No default
      }
    },
  );

  useEffect(() => {
    refToListenTo?.addEventListener("keydown", handleKeyPress);
    return () => {
      refToListenTo?.removeEventListener("keydown", handleKeyPress);
    };
  }, [refToListenTo, handleKeyPress]);

  const itemSelectedStable = useHandler(itemSelected);
  const setFocusedIndexStable = useHandler(setFocusedIndex);

  const sortedItems = useMemo(() => {
    if (sortBySelected) {
      return [...items].sort((a, b) => {
        const aSelected = isItemSelected(a);
        const bSelected = isItemSelected(b);

        if (aSelected === bSelected) return 0;
        return aSelected ? -1 : 1;
      });
    }
    return items;
  }, [items, isItemSelected, sortBySelected]);

  if (virtualize && !itemsLoading && sortedItems.length > 0) {
    return (
      <Flex grow={1} overflow="hidden">
        <VirtualizedListBody
          card={card}
          customStyles={customStyles}
          disabledItems={disabledItems}
          disabledTooltips={disabledTooltips}
          focusedIndex={focusedIndex}
          indeterminateItems={indeterminateItems}
          isItemDisabled={isItemDisabled}
          isItemIndeterminate={isItemIndeterminate}
          isItemSelected={isItemSelected}
          items={sortedItems}
          itemSelected={itemSelectedStable}
          onItemMouseEnter={onItemMouseEnter}
          onItemMouseLeave={onItemMouseLeave}
          onKeyDown={handleKeyPress}
          overscan={virtualOverscan}
          rowSize={virtualItemSize}
          scrollMaxHeight={virtualMaxHeight}
          selectedItems={selectedItems}
          selectionVariant={selectionVariant}
          setFocusedIndex={setFocusedIndexStable}
          size={size}
        />
      </Flex>
    );
  }

  return (
    <Flex grow={1} overflow="hidden">
      {itemsLoading ? (
        <LoadingList />
      ) : (
        <Flex
          className={redoListCss.childrenContainer}
          dir="column"
          gap="sm"
          onKeyDown={handleKeyPress}
          tabIndex={0}
        >
          {sortedItems.length === 0 && (
            <EmptyList message={emptyListMessage} size={size} />
          )}
          {sortedItems.map((item, idx) => (
            <ListItemRender
              card={card}
              customStyles={customStyles}
              disabled={isItemDisabled(item)}
              disabledItems={disabledItems}
              disabledTooltips={disabledTooltips}
              focused={focusedIndex === idx}
              idx={idx}
              indeterminate={isItemIndeterminate(item)}
              indeterminateItems={indeterminateItems}
              item={item}
              itemSelected={itemSelectedStable}
              key={getKey(item.id)}
              onItemMouseEnter={onItemMouseEnter}
              onItemMouseLeave={onItemMouseLeave}
              selected={isItemSelected(item)}
              selectedItems={selectedItems}
              selectionVariant={selectionVariant}
              setFocusedIndex={setFocusedIndexStable}
              size={size}
            />
          ))}
        </Flex>
      )}
    </Flex>
  );
});

interface VirtualizedListBodyProps<T> {
  items: RedoListItem<T>[];
  focusedIndex: number | undefined;
  setFocusedIndex: Dispatch<SetStateAction<number | undefined>>;
  itemSelected(args: {
    item: RedoListItem<T>;
    source: RedoListItemSelectedSource;
    index: number;
    selected: boolean;
  }): void;
  isItemDisabled: (item: RedoListItem<T>) => boolean;
  isItemSelected: (item: RedoListItem<T>) => boolean;
  isItemIndeterminate: (item: RedoListItem<T>) => boolean;
  selectedItems?: (string | number | symbol)[] | (string | number | symbol);
  indeterminateItems?:
    | (string | number | symbol)[]
    | (string | number | symbol);
  disabledItems?: (string | number | symbol)[] | (string | number | symbol);
  disabledTooltips?: Record<string | number, string>;
  selectionVariant: RedoListItemSelectionVariant;
  size: RedoListItemSize;
  card: boolean;
  customStyles?: React.CSSProperties;
  onItemMouseEnter?: (item: RedoListItem<T>) => void;
  onItemMouseLeave?: () => void;
  onKeyDown: (event: React.KeyboardEvent) => void;
  rowSize: number;
  scrollMaxHeight: number;
  overscan: number;
}

const VirtualizedListBody = genericMemo(function VirtualizedListBody<T>({
  items,
  focusedIndex,
  setFocusedIndex,
  itemSelected,
  isItemDisabled,
  isItemSelected,
  isItemIndeterminate,
  selectedItems,
  indeterminateItems,
  disabledItems,
  disabledTooltips,
  selectionVariant,
  size,
  card,
  customStyles,
  onItemMouseEnter,
  onItemMouseLeave,
  onKeyDown,
  rowSize,
  scrollMaxHeight,
  overscan,
}: VirtualizedListBodyProps<T>) {
  const scrollRef = useRef<HTMLDivElement>(null);

  const virtualizer = useVirtualizer({
    count: items.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => rowSize,
    overscan,
  });

  // Scroll the focused row into view when keyboard navigation moves past the
  // currently mounted window.
  useEffect(() => {
    if (focusedIndex === undefined) {
      return;
    }
    virtualizer.scrollToIndex(focusedIndex, { align: "auto" });
  }, [focusedIndex, virtualizer]);

  const virtualItems = virtualizer.getVirtualItems();

  return (
    <div
      className={redoListCss.virtualScrollArea}
      onKeyDown={onKeyDown}
      ref={scrollRef}
      style={{ maxHeight: scrollMaxHeight }}
      tabIndex={0}
    >
      <div
        style={{
          height: virtualizer.getTotalSize(),
          position: "relative",
          width: "100%",
        }}
      >
        {virtualItems.map((virtualRow) => {
          const item = items[virtualRow.index];
          if (!item) {
            return null;
          }
          return (
            <div
              data-index={virtualRow.index}
              key={getKey(item.id)}
              ref={virtualizer.measureElement}
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                transform: `translateY(${virtualRow.start}px)`,
                paddingBottom: 4,
              }}
            >
              <ListItemRender
                card={card}
                customStyles={customStyles}
                disabled={isItemDisabled(item)}
                disabledItems={disabledItems}
                disabledTooltips={disabledTooltips}
                focused={focusedIndex === virtualRow.index}
                idx={virtualRow.index}
                indeterminate={isItemIndeterminate(item)}
                indeterminateItems={indeterminateItems}
                item={item}
                itemSelected={itemSelected}
                onItemMouseEnter={onItemMouseEnter}
                onItemMouseLeave={onItemMouseLeave}
                selected={isItemSelected(item)}
                selectedItems={selectedItems}
                selectionVariant={selectionVariant}
                setFocusedIndex={setFocusedIndex}
                size={size}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
});

export const ListItemRender = genericMemo(function ListItemRender<T>({
  item,
  idx,
  setFocusedIndex,
  focused,
  itemSelected,
  selectionVariant,
  size,
  disabled,
  selected,
  indeterminate,
  card,
  useWrapper = true,
  indeterminateItems,
  selectedItems,
  disabledItems,
  disabledTooltips,
  customStyles,
  onItemMouseEnter,
  onItemMouseLeave,
}: {
  item: RedoListItem<T>;
  idx: number;
  focused: boolean;
  disabled: boolean;
  selected: boolean;
  indeterminate?: boolean;
  setFocusedIndex?: Dispatch<SetStateAction<number | undefined>>;
  itemSelected?(args: {
    item: RedoListItem<T>;
    source: RedoListItemSelectedSource;
    index: number;
    selected: boolean;
  }): void;
  selectionVariant: RedoListItemSelectionVariant;
  size: RedoListItemSize;
  card: boolean;
  useWrapper?: boolean;
  indeterminateItems?:
    | (string | number | symbol)[]
    | (string | number | symbol);
  selectedItems?: (string | number | symbol)[] | (string | number | symbol);
  disabledItems?: (string | number | symbol)[] | (string | number | symbol);
  disabledTooltips?: Record<string | number, string>;
  customStyles?: React.CSSProperties;
  onItemMouseEnter?: (item: RedoListItem<T>) => void;
  onItemMouseLeave?: () => void;
}) {
  const onItemHovered = useHandler((hovered: boolean) => {
    if (hovered && !disabled) {
      setFocusedIndex?.(idx);
      onItemMouseEnter?.(item);
    } else {
      setFocusedIndex?.((currentFocusedIndex) => {
        const thisItemIsFocused = currentFocusedIndex === idx;
        return thisItemIsFocused ? undefined : currentFocusedIndex;
      });
      onItemMouseLeave?.();
    }
  });

  const handleItemSelected = useHandler((selected: boolean) => {
    itemSelected?.({ item, source: "mouse", index: idx, selected });
  });

  const tooltipMessage =
    disabled && disabledTooltips && typeof item.id !== "symbol"
      ? disabledTooltips[item.id]
      : undefined;

  const resolvedSelectionVariant = item.selectionVariant ?? selectionVariant;

  if (item.type === "section") {
    const linkProps = item.externalLink
      ? { to: item.externalLink, target: "_blank" }
      : undefined;

    return (
      <Flex
        align="center"
        as={item.externalLink ? "a" : undefined}
        justify="space-between"
        linkProps={linkProps}
      >
        <Text
          fontSize={itemSizeToTextSize[size]}
          style={{ opacity: 1 }}
          textColor="tertiary"
        >
          {item.text}
        </Text>
        {item.externalLink && <RedoIcon Icon={ExternalLinkIcon} />}
      </Flex>
    );
  }

  if (item.type === "text") {
    const textItem = (
      <TextRedoListItem
        card={card}
        customStyles={customStyles}
        dataTestId={`dropdown-option-${item.text.replace(/['’`]/g, "")}`}
        disabled={disabled}
        focused={focused}
        href={item.href}
        {...item}
        hideOnHoverTooltip
        indeterminate={indeterminate}
        onItemHovered={setFocusedIndex ? onItemHovered : undefined}
        onItemSelected={itemSelected ? handleItemSelected : undefined}
        selected={selected}
        selectionVariant={resolvedSelectionVariant}
        size={size}
        useWrapper={useWrapper}
      />
    );

    return (
      <DisabledTooltip disabledMessage={tooltipMessage || false}>
        {textItem}
      </DisabledTooltip>
    );
  }
  if (item.type === "badge") {
    const badgeItem = (
      <BadgeRedoListItem
        badge={item.badge}
        card={card}
        customStyles={customStyles}
        disabled={disabled}
        focused={focused}
        hotkey={item.hotkey}
        indeterminate={indeterminate}
        menu={item.menu}
        onItemHovered={onItemHovered}
        onItemSelected={handleItemSelected}
        selected={selected}
        selectionVariant={resolvedSelectionVariant}
        size={size}
        useWrapper={useWrapper}
      />
    );

    return (
      <DisabledTooltip disabledMessage={tooltipMessage || false}>
        {badgeItem}
      </DisabledTooltip>
    );
  }
  if (item.type === "custom") {
    const customItem =
      item.useWrapper && useWrapper !== false ? (
        <RedoListItemContainer
          card={card}
          customStyles={customStyles}
          disabled={disabled}
          focused={focused}
          {...item}
          indeterminate={indeterminate}
          onItemHovered={onItemHovered}
          onItemSelected={handleItemSelected}
          selected={selected}
          selectionVariant={resolvedSelectionVariant}
          size={size}
        >
          {item.render}
        </RedoListItemContainer>
      ) : (
        <>{item.render}</>
      );

    return (
      <DisabledTooltip disabledMessage={tooltipMessage || false}>
        {customItem}
      </DisabledTooltip>
    );
  }

  if (item.type === "parent") {
    return (
      <ParentRedoListItem
        card={card}
        disabledItems={disabledItems}
        focused={focused}
        indeterminateItems={indeterminateItems}
        item={item}
        itemSelected={itemSelected}
        selectedItems={selectedItems}
        selectionVariant={resolvedSelectionVariant}
        size={size}
      />
    );
  }

  assertNever(item);
});

const EmptyList = memo(function EmptyList({
  message,
  size,
}: {
  message: string | undefined;
  size: RedoListItemSize;
}) {
  if (!message) {
    return null;
  }
  return (
    <Flex>
      <Text
        className={redoListItemCss.text}
        fontSize={itemSizeToTextSize[size]}
        fontWeight="regular"
        overflow="hidden"
        textColor="primary"
        textOverflow="ellipsis"
        whiteSpace="nowrap"
      >
        {message}
      </Text>
    </Flex>
  );
});

// Todo: replace with skeleton?
const LoadingList = memo(function LoadingList() {
  return (
    <Flex grow={1} justify="center">
      <Flex p="xl">
        <RedoSpinner style="line" />
      </Flex>
    </Flex>
  );
});
